import React, { useRef, useCallback, useEffect, useState } from 'react'
import ReactDOM from 'react-dom'
import DropdownMenu from './DropdownMenu'
import DropdownToggle from './DropdownToggle'
import PropTypes from 'prop-types'
import useUniqueId from '../hooks/useUniqueId'
import DropdownContext from './context/dropdownContext'
import DropdownMenuContext, {
    useDropdownMenuContext,
} from './context/dropdownMenuContext'
import chainedFunction from '../utils/chainedFunction'
import useRootClose from '../hooks/useRootClose'
import arrayIndexOf from '../utils/arrayIndexOf'
import { PLACEMENT } from '../utils/constant'
import classNames from 'classnames'

const CLICK = 'click'
const HOVER = 'hover'
const CONTEXT = 'context'
const PORTAL_MENU_ZOOM = 0.82

const Dropdown = React.forwardRef((props, ref) => {
    const {
        title,
        children,
        className,
        menuClass,
        menuStyle,
        disabled,
        renderTitle,
        parentClassName,
        placement,
        activeKey,
        toggleClassName,
        trigger,
        style,
        onClick,
        onMouseEnter,
        onMouseLeave,
        onContextMenu,
        onSelect,
        onOpen,
        onClose,
        onToggle,
        onKeyDown,
        preventClose,
        portal,
        ...rest
    } = props

    const overlayTarget = useRef()
    const triggerTarget = useRef()
    const menuControl = useDropdownMenuContext(overlayTarget)
    const open = menuControl.open

    // Portal mode: render the menu on document.body with fixed positioning
    // anchored to the toggle, so ancestors with overflow/stacking contexts
    // (scroll panes, overflow-hidden cards, modals) can never clip it.
    const [portalStyle, setPortalStyle] = useState({})

    useEffect(() => {
        if (!portal || !open) return

        const updatePosition = () => {
            const trigger = triggerTarget.current
            if (!trigger) return
            const rect = trigger.getBoundingClientRect()
            // Portal menus are compacted with CSS zoom; fixed offsets are
            // rendered multiplied by the zoom factor, so divide to land on
            // the intended viewport coordinates.
            const z = PORTAL_MENU_ZOOM
            const style = { position: 'fixed', zIndex: 99999, zoom: z, minWidth: `${rect.width / z}px` }
            const p = String(placement || '')
            if (p.startsWith('top')) {
                style.bottom = `${(window.innerHeight - rect.top + 4) / z}px`
            } else {
                style.top = `${(rect.bottom + 4) / z}px`
            }
            if (p.endsWith('-end')) {
                style.right = `${Math.max(window.innerWidth - rect.right, 8) / z}px`
            } else if (p.endsWith('-center')) {
                style.left = `${(rect.left + rect.width / 2) / z}px`
                style.transform = 'translateX(-50%)'
            } else {
                style.left = `${rect.left / z}px`
            }
            setPortalStyle(style)
        }

        updatePosition()
        window.addEventListener('resize', updatePosition)
        // Capture phase so scrolls inside any ancestor pane reposition the menu
        window.addEventListener('scroll', updatePosition, true)
        return () => {
            window.removeEventListener('resize', updatePosition)
            window.removeEventListener('scroll', updatePosition, true)
        }
    }, [portal, open, placement])

    const buttonId = useUniqueId('dropdown-toggle-')
    const menuId = useUniqueId('base-menu-')

    const handleToggle = useCallback(
        (isOpen) => {
            const nextOpen = typeof isOpen === 'undefined' ? !open : isOpen
            const fn = nextOpen ? onOpen : onClose

            fn?.()
            onToggle?.(nextOpen)
            if (nextOpen) {
                menuControl.openMenu()
            } else {
                menuControl.closeMenu()
            }
        },
        [onClose, onOpen, onToggle, open, menuControl]
    )

    const handleClick = useCallback(
        (e) => {
            e.preventDefault()
            if (disabled) {
                return
            }
            handleToggle()
        },
        [disabled, handleToggle]
    )

    const handleMouseEnter = useCallback(() => {
        if (!disabled) {
            handleToggle(true)
        }
    }, [disabled, handleToggle])

    const handleMouseLeave = useCallback(() => {
        if (!disabled) {
            handleToggle(false)
        }
    }, [disabled, handleToggle])

    const handleSelect = (eventKey, e) => {
        onSelect?.(eventKey, e)
        if (!preventClose) {
            handleToggle(false)
        }
    }

    useRootClose(() => {
        if (!preventClose) {
            handleToggle()
        }
    }, {
        triggerTarget,
        overlayTarget,
        disabled: !open || preventClose,
        listenEscape: !preventClose,
    })

    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Enter' && !preventClose) {
            handleToggle(false)
        }
        onKeyDown?.(e)
    }, [onKeyDown, handleToggle, preventClose])

    const dropdownProps = {
        onMouseEnter,
        onMouseLeave,
        onKeyDown: handleKeyDown
    }

    const toggleEventHandlers = {
        onClick: onClick,
        onContextMenu,
    }

    if (arrayIndexOf(CLICK, trigger)) {
        toggleEventHandlers.onClick = chainedFunction(
            handleClick,
            toggleEventHandlers.onClick
        )
    }

    if (arrayIndexOf(CONTEXT, trigger)) {
        toggleEventHandlers.onContextMenu = chainedFunction(
            handleClick,
            onContextMenu
        )
    }

    if (arrayIndexOf(HOVER, trigger)) {
        dropdownProps.onMouseEnter = chainedFunction(
            handleMouseEnter,
            onMouseEnter
        )
        dropdownProps.onMouseLeave = chainedFunction(
            handleMouseLeave,
            onMouseLeave
        )
    }

    const toggleElement = (
        <DropdownToggle
            {...rest}
            {...toggleEventHandlers}
            id={buttonId}
            ref={triggerTarget}
            className={toggleClassName}
            renderTitle={renderTitle}
            disabled={disabled}
            placement={placement}
        >
            {title}
        </DropdownToggle>
    )

    const menuElement = (
        <DropdownMenu
            className={menuClass}
            style={portal ? { ...menuStyle, ...portalStyle } : menuStyle}
            onSelect={handleSelect}
            activeKey={activeKey}
            ref={overlayTarget}
            hidden={!open}
            placement={portal ? undefined : placement}
            id={menuId}
        >
            {children}
        </DropdownMenu>
    )

    return (
        <DropdownContext.Provider value={{ activeKey }}>
            <div
                {...dropdownProps}
                ref={ref}
                style={style}
                className={classNames(
                    "dropdown",
                    parentClassName
                )}
            >
                {toggleElement}
                <DropdownMenuContext.Provider value={menuControl}>
                    {portal && typeof document !== 'undefined'
                        ? ReactDOM.createPortal(menuElement, document.body)
                        : menuElement}
                </DropdownMenuContext.Provider>
            </div>
        </DropdownContext.Provider>
    )
})

const {
    TOP_START,
    TOP_CENTER,
    TOP_END,
    BOTTOM_START,
    BOTTOM_CENTER,
    BOTTOM_END,
    MIDDLE_START_TOP,
    MIDDLE_START_BOTTOM,
    MIDDLE_END_TOP,
    MIDDLE_END_BOTTOM,
} = PLACEMENT

Dropdown.propTypes = {
    trigger: PropTypes.oneOf([CLICK, HOVER, CONTEXT]),
    placement: PropTypes.oneOf([
        TOP_START,
        TOP_CENTER,
        TOP_END,
        BOTTOM_START,
        BOTTOM_CENTER,
        BOTTOM_END,
        MIDDLE_START_TOP,
        MIDDLE_START_BOTTOM,
        MIDDLE_END_TOP,
        MIDDLE_END_BOTTOM,
    ]),
    menuClass: PropTypes.string,
    menuStyle: PropTypes.object,
    disabled: PropTypes.bool,
    title: PropTypes.string,
    renderTitle: PropTypes.node,
    activeKey: PropTypes.string,
    toggleClassName: PropTypes.string,
    onClick: PropTypes.func,
    onMouseEnter: PropTypes.func,
    onMouseLeave: PropTypes.func,
    onContextMenu: PropTypes.func,
    onSelect: PropTypes.func,
    onOpen: PropTypes.func,
    onClose: PropTypes.func,
    onToggle: PropTypes.func,
    onKeyDown: PropTypes.func,
    preventClose: PropTypes.bool,
    portal: PropTypes.bool,
}

Dropdown.defaultProps = {
    placement: BOTTOM_START,
    trigger: 'click',
    preventClose: false,
    portal: false,
}

export default Dropdown
